import { ReactSVG } from 'react-svg'
import classNames from 'classnames'
import { useLayoutEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'

export interface Props {
  items: number
  disabled?: boolean
  value?: number
  onChange?: (value: number) => void
}

const SlideControl = ({
  value = 0,
  onChange,
  items,
  disabled = false
}: Props) => {
  const bar = useRef<HTMLSpanElement>(null)
  const itemsRef = useRef<HTMLDivElement>(null)
  const handle = useRef<HTMLSpanElement>(null)
  const [progress, setProgress] = useState(0)
  const [progressAbsolute, setProgressAbsolute] = useState(0)

  const getProgress = (value: number, absolute = false) => {
    if (value === 0) return 0

    const item = itemsRef.current?.children[value - 1]
    const itemsLeft = itemsRef.current?.getBoundingClientRect().left ?? 0
    const itemLeft =
      (item?.getBoundingClientRect().left ?? 0) -
      itemsLeft +
      (item?.getBoundingClientRect().width ?? 0)

    if (absolute) return Math.max(0, itemLeft - 8)

    return (
      (itemLeft / (itemsRef.current?.getBoundingClientRect().width ?? 0)) * 100
    )
  }

  useLayoutEffect(() => {
    const handler = () => {
      setProgress(getProgress(value))
      setProgressAbsolute(getProgress(value, true))
    }

    window.addEventListener('resize', handler)
    handler()

    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [value, items])

  useLayoutEffect(() => {
    const handleEl = handle.current
    if (!handleEl) return

    const mouse = {
      x: 0,
      y: 0
    }

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      mouse.x = 'touches' in e ? e.touches[0].clientX : e.clientX
      mouse.y = 'touches' in e ? e.touches[0].clientY : e.clientY

      const items = Array.from(itemsRef.current?.children ?? [])

      const res = items.findIndex((el) => {
        const rect = el.getBoundingClientRect()
        return (
          mouse.x > rect.left + rect.width / 2 &&
          mouse.x < rect.right + rect.width / 2
        )
      })

      if (res >= 0) {
        onChange?.(res + 1)
      }
    }

    const handleMouseUp = (e: MouseEvent | TouchEvent) => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('touchmove', handleMouseMove)

      gsap.set(document.documentElement, { clearProps: 'cursor' })
      gsap.set(handleEl, { cursor: 'grab' })
    }

    const handler = () => {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('touchmove', handleMouseMove)

      document.addEventListener('mouseup', handleMouseUp, { once: true })
      document.addEventListener('touchend', handleMouseUp, { once: true })

      gsap.set(document.documentElement, { cursor: 'grabbing' })
      gsap.set(handleEl, { cursor: 'grabbing' })
    }

    handleEl.addEventListener('mousedown', handler)
    handleEl.addEventListener('touchstart', handler)

    return () => {
      handleEl.removeEventListener('mousedown', handler)
      handleEl.removeEventListener('touchstart', handler)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchend', handleMouseUp)
      gsap.set(document.documentElement, { clearProps: 'cursor' })
      gsap.set(handleEl, { cursor: 'grab' })
    }
  }, [disabled])

  return (
    <div className={classNames('slide-control', { disabled })}>
      <div className={'slide-control__progress'}>
        <div className={'slide-control__progress-bar'}>
          <span
            ref={bar}
            style={{ transform: `scaleX(${progress / 100})` }}
          ></span>
        </div>
        <div
          className={'slide-control__progress-handle'}
          style={{ transform: `translateX(${progressAbsolute}px)` }}
        >
          <span ref={handle}></span>
        </div>
      </div>

      <div ref={itemsRef} className={'slide-control__items'}>
        {Array.from({ length: items }).map((_, index: number) => (
          <div key={index}>
            <button
              type={'button'}
              onClick={() => !disabled && onChange?.(index + 1)}
              aria-label={`Оценка ${index + 1}`}
              className={classNames({ active: value >= index + 1 })}
            >
              <span className={'slide-control__stars-desktop'}>
                {Array.from({ length: index + 1 }).map((_, starIndex) => (
                  <ReactSVG
                    key={starIndex}
                    src={'/i/svg/rating.svg'}
                    wrapper={'span'}
                  />
                ))}
              </span>
              <span className={'slide-control__stars-mobile'}>
                <ReactSVG src={'/i/svg/rating.svg'} wrapper={'span'} />
              </span>
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export { SlideControl }
